import { type LucideIcon, RefreshCw, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../actions/Button.tsx';
import { Icon } from '../display/Icon.tsx';
import styles from './StateBlock.module.css';

export interface ErrorStateProps {
  /** What happened, in people's words. */
  readonly title: string;
  /** What to do about it. */
  readonly description?: string | undefined;
  readonly icon?: LucideIcon | undefined;
  /** Offered when trying again can help. */
  readonly onRetry?: (() => void) | undefined;
  readonly retrying?: boolean | undefined;
  /** A different way out, e.g. a link home. */
  readonly action?: ReactNode | undefined;
}

export const ErrorState = ({
  title,
  description,
  icon = TriangleAlert,
  onRetry,
  retrying,
  action,
}: ErrorStateProps) => {
  const { t } = useTranslation();
  return (
    <div className={styles.block} role="alert">
      <span className={styles.badgeDanger}>
        <Icon icon={icon} size="lg" />
      </span>
      <h2 className={styles.title}>{title}</h2>
      {description && <p className={styles.description}>{description}</p>}
      {(onRetry || action) && (
        <div className={styles.action}>
          {onRetry && (
            <Button icon={RefreshCw} onClick={onRetry} loading={retrying ?? false}>
              {t('ui.retry')}
            </Button>
          )}
          {action}
        </div>
      )}
    </div>
  );
};
